import React from "react";
import ArgonBox from "components/ArgonBox";
import Table from "examples/Tables/Table";
import { Card, CircularProgress, Alert } from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Argon Dashboard 2 MUI examples
import Footer from "examples/Footer";

import { useState, useEffect } from "react";

import TutoProfileCard from "./TutoProfileCard";
import PaymentsTableData from "./data/PaymentsTableData";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ArgonTypography from "components/ArgonTypography";

import { Dialog, DialogTitle, DialogContent, Grid, DialogActions } from "@mui/material";

import Dropdown from "components/CustomDropdown/Dropdown";
import { useParams } from "react-router-dom";
import api from "utils/api";
import { toast } from "react-toastify";

function BasicDetails() {
  const { id } = useParams();
  const { columns, rows } = PaymentsTableData(id);

  // Registration fee states
  const [tutorData, setTutorData] = useState(null);
  const [registrationFees, setRegistrationFees] = useState(0);
  const [loadingTutor, setLoadingTutor] = useState(true);
  const [isRegFeeModalOpen, setIsRegFeeModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("ONLINE");
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Fetch tutor data and registration fees
  useEffect(() => {
    const fetchTutorAndFees = async () => {
      setLoadingTutor(true);
      try {
        // Fetch tutor data
        const tutorResponse = await api.get(`/tutor/${id}`);
        if (tutorResponse.data.success) {
          setTutorData(tutorResponse.data.data);
        }

        // Fetch registration fee status & configured amount using dedicated endpoint
        try {
          const statusResp = await api.get(`/tutor/${id}/registration-fee`);
          if (statusResp.data?.success && statusResp.data.data) {
            const { registrationFeePaid, registrationFeesAmount } = statusResp.data.data;
            // ensure tutorData reflects actual paid flag from status endpoint
            setTutorData((prev) => ({ ...(prev || {}), registrationFee: registrationFeePaid }));
            setRegistrationFees(registrationFeesAmount || 0);
          }
        } catch (e) {
          // fallback to plan endpoint if status endpoint fails
          const feesResponse = await api.get("/planwithstudentreview");
          if (feesResponse.data.success) {
            const fees = feesResponse.data.data.registrationFees || 0;
            setRegistrationFees(fees);
          }
        }
      } catch (error) {
        console.error("Error fetching tutor/fees data:", error);
        toast.error("Failed to load tutor data");
      } finally {
        setLoadingTutor(false);
      }
    };

    if (id) {
      fetchTutorAndFees();
    }
  }, [id]);

  // Handle registration fee payment
  const handlePayRegistrationFee = async () => {
    setPaymentLoading(true);
    try {
      // Backend requires a transactionId. If paying by CASH (or for quick local testing),
      // generate a client-side id. In production with a payment gateway, use the real gateway txn id.
      const transactionId = `${paymentMethod}_${Date.now()}`;

      const response = await api.post(`/tutor/${id}/registration-fee`, {
        method: paymentMethod,
        transactionId,
      });

      if (response.data.success) {
        toast.success("Registration fee paid successfully!");
        setIsRegFeeModalOpen(false);
        // Refresh tutor data
        const tutorResponse = await api.get(`/tutor/${id}`);
        if (tutorResponse.data.success) {
          setTutorData(tutorResponse.data.data);
        }
      }
    } catch (error) {
      console.error("Error paying registration fee:", error);
      toast.error(error.response?.data?.message || "Failed to process payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  // NOTE: Do not return early before declaring all hooks below —
  // that causes "Rendered fewer hooks than expected" errors.

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleAddPayroll = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [search, setSearch] = useState("");

  const [selectedTutor, setSelectedTutor] = useState("");
  const [classList, setClassList] = useState("");
  const [cycle, setcycle] = useState("");

  // If there are no payments and tutor data finished loading, show a friendly empty state.
  if (rows.length === 0 && !loadingTutor) {
    return (
      <ArgonBox>
        <Card sx={{ p: 5 }}>
          <ArgonBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
          >
            <AccountBalanceWalletIcon sx={{ fontSize: 64, color: "#d0d0d0" }} />
            <ArgonTypography variant="h6" color="text">
              No Payment Data Available
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text" textAlign="center">
              This tutor hasn&#39;t received any payments yet.
            </ArgonTypography>
          </ArgonBox>
        </Card>
      </ArgonBox>
    );
  }

  return (
    <ArgonBox>
      {/* Registration Fee Status Card */}
      {loadingTutor ? (
        <Card sx={{ p: 3, mb: 3 }}>
          <ArgonBox display="flex" justifyContent="center" alignItems="center" py={2}>
            <CircularProgress />
          </ArgonBox>
        </Card>
      ) : (
        <Card sx={{ p: 3, mb: 3 }}>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
            <ArgonBox display="flex" alignItems="center" gap={2}>
              {tutorData?.registrationFee ? (
                <>
                  <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
                  <ArgonBox>
                    <ArgonTypography variant="h6" fontWeight="bold">
                      Registration Fee Paid
                    </ArgonTypography>
                    <ArgonTypography variant="caption" color="text">
                      Amount: ₹{registrationFees}
                    </ArgonTypography>
                  </ArgonBox>
                </>
              ) : (
                <>
                  <AccountBalanceWalletIcon sx={{ fontSize: 40, color: "warning.main" }} />
                  <ArgonBox>
                    <ArgonTypography variant="h6" fontWeight="bold">
                      Registration Fee Pending
                    </ArgonTypography>
                    <ArgonTypography variant="caption" color="text">
                      Amount: ₹{registrationFees}
                    </ArgonTypography>
                  </ArgonBox>
                </>
              )}
            </ArgonBox>
            {!tutorData?.registrationFee && (
              <ArgonButton
                variant="contained"
                color="primary"
                onClick={() => setIsRegFeeModalOpen(true)}
              >
                Pay Registration Fee
              </ArgonButton>
            )}
          </ArgonBox>
        </Card>
      )}

      {/* <TutoProfileCard/> */}

      <ArgonBox>
        <ArgonBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mx={3}
          mt={5}
          mb={2}
        >
          <ArgonTypography variant="body2" color="text" fontWeight="medium">
            Add Payroll
          </ArgonTypography>

          <ArgonBox display="flex" alignItems="center" gap={2}>
            {/* <ArgonButton
              onClick={handleAddPayroll}
              variant="contained"
              color="primary"
              size="small"
            >
              Add
            </ArgonButton> */}
            <ArgonInput placeholder="Search" />
            <ArrowCircleDownIcon
              sx={{
                fontSize: "38px !important",
                color: "#1976d2",
                padding: "8px",
                backgroundColor: "#e3f2fd",
                borderRadius: "50%",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#bbdefb",
                  transform: "scale(1.1)",
                },
              }}
            />
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>

      <ArgonBox
        mt={5}
        sx={{
          "& .MuiTableRow-root:not(:last-child)": {
            "& td": {
              borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                `${borderWidth[1]} solid ${borderColor}`,
            },
          },
        }}
      >
        <Table columns={columns} rows={rows} />
      </ArgonBox>

      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <ArgonTypography variant="h5" fontWeight="bold">
            Tutor Draft Salary Add
          </ArgonTypography>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Tutor */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="body2" fontWeight="medium" color="text" mb={1}>
                Tutor
              </ArgonTypography>
              <Dropdown
                label=""
                value={selectedTutor}
                options={["John Doe", "Jane Smith", "Alice Johnson"]}
                onChange={(e) => setSelectedTutor(e.target.value)}
                width={300}
              />
            </Grid>

            {/* Class */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="body2" fontWeight="medium" color="text" mb={1}>
                Class
              </ArgonTypography>
              <Dropdown
                value={classList}
                options={["Math Class", "Science Class", "English Class"]}
                onChange={(e) => setClassList(e.target.value)}
                width={300}
              />
            </Grid>

            {/* Cycle */}
            <Grid item xs={12}>
              <ArgonTypography variant="body2" fontWeight="medium" color="text" mb={1}>
                Cycle
              </ArgonTypography>
              <Dropdown
                value={cycle}
                options={["Cycle 1", "Cycle 2", "Cycle 3"]}
                onChange={(e) => setcycle(e.target.value)}
                width={680}
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={12} md={6}>
              <ArgonInput fullWidth label="Amount" placeholder="Enter Amount" />
            </Grid>

            {/* Bonus Note */}
            <Grid item xs={12} md={6}>
              <ArgonInput fullWidth label="Bonus Note" placeholder="Enter Bonus Note" />
            </Grid>

            {/* Total Bonus */}
            <Grid item xs={12} md={6}>
              <ArgonInput fullWidth label="Total Bonus Amount" placeholder="Enter Bonus Amount" />
            </Grid>

            {/* Deduction Note */}
            <Grid item xs={12} md={6}>
              <ArgonInput fullWidth label="Deduction Note" placeholder="Enter Deduction Note" />
            </Grid>

            {/* Deduction Amount */}
            <Grid item xs={12} md={6}>
              <ArgonInput
                fullWidth
                label="Total Deduction Amount"
                placeholder="Enter Deduction Amount"
              />
            </Grid>

            {/* Cycle Completion Date */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="body2" fontWeight="medium" color="text" mb={1}>
                Cycle Completion Date
              </ArgonTypography>
              <ArgonInput fullWidth type="date" label="Cycle Completion Date" />
            </Grid>

            {/* Payment Due Date */}
            <Grid item xs={12} md={6}>
              <ArgonTypography variant="body2" fontWeight="medium" color="text" mb={1}>
                Payment Due Date
              </ArgonTypography>
              <ArgonInput fullWidth type="date" label="Payment Due Date" />
            </Grid>

            {/* Remark */}
            <Grid item xs={12} md={6}>
              <ArgonInput fullWidth multiline rows={3} label="Remark" placeholder="Enter Remark" />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <ArgonButton variant="outlined" color="secondary" onClick={handleCloseModal}>
            Cancel
          </ArgonButton>
          <ArgonButton variant="contained" color="primary" onClick={handleCloseModal}>
            Save
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Registration Fee Payment Modal */}
      <Dialog
        open={isRegFeeModalOpen}
        onClose={() => !paymentLoading && setIsRegFeeModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <ArgonTypography variant="h5" fontWeight="bold">
            Pay Registration Fee
          </ArgonTypography>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Tutor Name */}
            <Grid item xs={12}>
              <ArgonTypography variant="body2" fontWeight="medium" color="text" mb={1}>
                Tutor Name
              </ArgonTypography>
              <ArgonInput fullWidth value={tutorData?.fullName || ""} disabled />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12}>
              <ArgonTypography variant="body2" fontWeight="medium" color="text" mb={1}>
                Payment Method
              </ArgonTypography>
              <Dropdown
                value={paymentMethod}
                options={["ONLINE", "CASH"]}
                onChange={(e) => setPaymentMethod(e.target.value)}
                width="100%"
              />
            </Grid>

            {/* Amount Display (Read-only) */}
            <Grid item xs={12}>
              <ArgonTypography variant="body2" fontWeight="medium" color="text" mb={1}>
                Registration Fee Amount
              </ArgonTypography>
              <ArgonInput fullWidth type="text" value={`₹${registrationFees}`} disabled />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                The registration fee will be marked as paid and a transaction will be created.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <ArgonButton
            variant="outlined"
            color="secondary"
            onClick={() => setIsRegFeeModalOpen(false)}
            disabled={paymentLoading}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            variant="contained"
            color="primary"
            onClick={handlePayRegistrationFee}
            disabled={paymentLoading}
          >
            {paymentLoading ? <CircularProgress size={20} /> : "Pay Now"}
          </ArgonButton>
        </DialogActions>
      </Dialog>
    </ArgonBox>
  );
}

export default BasicDetails;
